import * as React from 'react';
import { useEffect } from 'react';

import { translate } from '~l10n/client';

import { PaywallTouchEventAdapter } from '~analytics/index';
import {
  Dialog,
  DialogConfirmation,
  DialogProps,
} from '~shared/components/Dialog';
import { Inline } from '~shared/components/Inline';
import { Stack } from '~shared/components/Stack';
import { ReactNodeNoStrings } from '~shared/types';

import { PaywallTouch } from './usePaywallReducer';

export interface Props extends Omit<DialogProps, 'children' | 'padding'> {
  children: React.ReactNode;
  actions: ReactNodeNoStrings[];
  paywallTouch: PaywallTouch;
}

function usePaywallTouchEvent({
  isOpen,
  paywallTouch,
}: {
  isOpen: boolean;
  paywallTouch: PaywallTouch;
}) {
  useEffect(() => {
    const [paywallScreen, secondaryFeature] = paywallTouch;
    if (isOpen) {
      new PaywallTouchEventAdapter(
        paywallScreen,
        secondaryFeature
      ).sendAnalytics();
    }
  }, [isOpen, paywallTouch]);
}
export function LongPaywallDialog({
  children,
  actions,
  paywallTouch,
  ...props
}: Props) {
  usePaywallTouchEvent({ isOpen: props.open, paywallTouch: paywallTouch });

  return (
    <Dialog
      padding="confirmation"
      closeLabel={translate('Close')}
      open={open}
      {...props}
    >
      {(headerProps) => (
        <div className="">
          <Stack space="4">
            <h3 className="m-0">
              <span
                ref={headerProps.headingRef}
                className="font-display text-md font-500 leading-10 text-blue-900 outline-none"
                tabIndex={-1}
              >
                {headerProps.title}
              </span>
            </h3>
            <div className="text-base text-blue-900 leading-8">{children}</div>
          </Stack>
          <div className="pt-8">
            <Inline align="right" alignY="center">
              {actions.map((action) => action)}
            </Inline>
          </div>
        </div>
      )}
    </Dialog>
  );
}
export function ShortPaywallDialog({
  children,
  actions,
  paywallTouch,
  ...props
}: Props) {
  usePaywallTouchEvent({ isOpen: props.open, paywallTouch: paywallTouch });

  return (
    <DialogConfirmation actions={actions} {...props}>
      {children}
    </DialogConfirmation>
  );
}

export const PaywallDialog = {
  Long: LongPaywallDialog,
  Short: ShortPaywallDialog,
};
